import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Typography, Table, Progress, Statistic, Divider, Select, Space, Radio, Button, Timeline, Tag, Modal, DatePicker, Tabs, Collapse, List, Calendar, Badge, Input, Empty, Descriptions, Alert, Slider } from 'antd';
import { 
  CarOutlined, 
  EnvironmentOutlined, 
  AimOutlined, 
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  RollbackOutlined,
  FilterOutlined,
  CalendarOutlined,
  BarChartOutlined,
  TableOutlined,
  FundViewOutlined,
  FileSearchOutlined,
  DotChartOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import dayjs from '../../../dayjs';
import useAllStops from '../../../hooks/useAllStops';
import useAllRoutes from '../../../hooks/useAllRoutes';
import './TripStopPerformance.css';
import { keyBy } from 'lodash';
import { secsSinceMidnightToDayjs } from '../../../model/timeFilter';
import useAllServices from '../../../hooks/useServices';
import { StopTimeAnalysis } from './StopTimeAnalysis';
import { ServiceAnalysis } from './ServiceAnalysis';
import { processStopData } from '../../../utils/stopDataProcessor';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Panel } = Collapse;

/**
 * TripStopPerformance Component
 * Displays detailed OTR performance metrics for trips and stops
 */
const TripStopPerformance = ({ reportData, dateRange }) => {
  // Import real data sources where available
  const { allStopsByStopCode } = useAllStops();
  const { allRoutes, allRoutesArray } = useAllRoutes();
  const { allServices } = useAllServices();

  // Component state
  const [displayMode, setDisplayMode] = useState('trips');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripDetailVisible, setTripDetailVisible] = useState(false);
  const [tripDateRange, setTripDateRange] = useState(dateRange);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopDetailVisible, setStopDetailVisible] = useState(false);
  const [stopDetailDate, setStopDetailDate] = useState(null);
  const [aggregatedStopData, setAggregatedStopData] = useState([]);
  const [stopPerformanceByDate, setStopPerformanceByDate] = useState({});
  const [stopDateRange, setStopDateRange] = useState(dateRange);
  const [stopFilterText, setStopFilterText] = useState('');
  const [tripsData, setTripsData] = useState([]);
  const [stopModalVisible, setStopModalVisible] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [showServiceInsights, setShowServiceInsights] = useState(false);
  const [selectedInsightRoute, setSelectedInsightRoute] = useState(null);
  const [selectedInsightTrip, setSelectedInsightTrip] = useState(null);
  const [showStopTimeAnalysis, setShowStopTimeAnalysis] = useState(false);
  const [selectedAnalysisRoute, setSelectedAnalysisRoute] = useState(null);
  const [selectedAnalysisTrip, setSelectedAnalysisTrip] = useState(null);
  const [showHybridAnalysis, setShowHybridAnalysis] = useState(false);
  const [selectedHybridRoute, setSelectedHybridRoute] = useState(null);
  const [selectedHybridTrip, setSelectedHybridTrip] = useState(null);
  const [selectedHybridOTR, setSelectedHybridOTR] = useState(95);

  // Helper function to determine stop position - memoized to prevent unnecessary recreations
  const determineStopPosition = useCallback((position) => {
    if (!position) return 'Unknown';
    
    // Normalize position
    const normalizedPosition = position.toString().toLowerCase();
    
    if (normalizedPosition.includes('first')) return 'First';
    if (normalizedPosition.includes('last')) return 'Last';
    
    return 'Middle';
  }, []);
  
  // Format percentage values
  const formatPercent = useCallback((value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return `${Math.min(value, 100).toFixed(2)}%`;
  }, []);
  
  // Handle stop selection
  const handleStopSelect = useCallback((stop, date = null) => {
    setSelectedStop(stop);
    setStopDetailDate(date);
    setStopDetailVisible(true);
  }, []);
  
  // Optimized aggregation of stop data
  const aggregateStopData = useCallback((parsedData) => {
    if (!parsedData || !parsedData.length || !allStopsByStopCode) return;
    
    // Create a map to store stop data by ID
    const stopMap = new Map();
    
    // Process all records in parsed data
    parsedData.forEach(record => {
      if (!record.stopId) return;
      const busableStop = allStopsByStopCode[record.stopId];
      const stopId = record.stopId;
      const stopName = busableStop?.stopName || record.stopName || `Stop ${stopId}`;
      const date = record.tripDate || 'Unknown Date';
      
      // If this stop is not in the map yet, create a new entry
      if (!stopMap.has(stopId)) {
        stopMap.set(stopId, {
          stopId,
          stopName,
          isTimingPoint: false,
          isHighTraffic: false,
          primaryPosition: determineStopPosition(record.stopPosition),
          totalOccurrences: 0,
          onTimeCount: 0,
          trackedArrivalCount: 0,
          trackedDepartureCount: 0,
          datePerformanceArray: [],
          datePerformanceMap: new Map()
        });
      }
      
      const stop = stopMap.get(stopId);
      
      // If we don't have data for this date yet, create a new entry
      if (!stop.datePerformanceMap.has(date)) {
        stop.datePerformanceMap.set(date, {
          date,
          totalOccurrences: 0,
          onTimeCount: 0,
          otrPercentage: 0,
          records: []
        });
      }
      
      const dateData = stop.datePerformanceMap.get(date);
      
      // Determine if this service was on time
      const isOnTime = record.timingStatus === 'on-time';
      
      // Update counters
      stop.totalOccurrences++;
      if (isOnTime) stop.onTimeCount++;
      if (record.trackedArrival) stop.trackedArrivalCount++;
      if (record.trackedDeparture) stop.trackedDepartureCount++;
      
      dateData.totalOccurrences++;
      if (isOnTime) dateData.onTimeCount++;
      
      // Add the record to the date's records
      dateData.records.push({
        tripId: record.tripId,
        scheduledTime: record.scheduledDepartureTime || record.scheduledArrivalTime || record.scheduledTime || 'Unknown',
        actualTime: record.actualDepartureTime || record.actualArrivalTime || 'Unknown',
        status: record.timingStatus,
        varianceInSeconds: record.varianceInSeconds
      });
      
      // Update OTR percentage for this date
      dateData.otrPercentage = (dateData.onTimeCount / dateData.totalOccurrences) * 100;
    });
    
    // Convert map to array and calculate overall OTR percentage
    const stopArray = Array.from(stopMap.values()).map(stop => {
      // Convert date performance map to array
      stop.datePerformanceArray = Array.from(stop.datePerformanceMap.values());
      
      // Calculate overall OTR percentage
      stop.otrPercentage = stop.totalOccurrences > 0 
        ? (stop.onTimeCount / stop.totalOccurrences) * 100 
        : 0;
      
      // Calculate trackedArrival and trackedDeparture percentages
      stop.trackedArrivalPercentage = stop.totalOccurrences > 0 ? (stop.trackedArrivalCount / stop.totalOccurrences) * 100 : 0;
      stop.trackedDeparturePercentage = stop.totalOccurrences > 0 ? (stop.trackedDepartureCount / stop.totalOccurrences) * 100 : 0;
      
      // Clean up the map property since we don't need it anymore
      delete stop.datePerformanceMap;

      // Get the stop object using the correct lookup method
      const stopObject = allStopsByStopCode[stop.stopId];
      
      // Return enhanced stop data
      return {
        ...stop,
        stopName: stopObject?.stopName || stop.stopName,
        isTimingPoint: stopObject?.isTimingPoint || stop.isTimingPoint,
        isHighTraffic: stopObject?.isHighTraffic || stop.isHighTraffic,
        stop: stopObject
      };
    });
    
    // Sort by OTR percentage (worst first)
    stopArray.sort((a, b) => a.otrPercentage - b.otrPercentage);
    
    setAggregatedStopData(stopArray);
  }, [allStopsByStopCode, determineStopPosition]);

  // Process trips data using optimized lookups
  const processTripsData = useCallback(() => {
    if (!reportData?.parsedData?.length || !allServices || !allRoutes) return;
    
    // Group services (trips) by tripId
    const tripGroups = {};
    
    reportData.parsedData.forEach(record => {
      if (!record.tripId) return;
      
      if (!tripGroups[record.tripId]) {
        // Use optimized lookups
        const serviceInfo = allServices[record.tripId] || {};
        const routeInfo = serviceInfo.routeId ? allRoutes[serviceInfo.routeId] || {} : {};
        const startTime = serviceInfo?.stopTimes?.[0]?.departureTime ? secsSinceMidnightToDayjs(serviceInfo?.stopTimes?.[0]?.departureTime).format('HH:mm') : record.scheduledTime ? dayjs(record.scheduledTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm') : '--:--';
        const direction = serviceInfo.direction || record.direction || 'Unknown';
        const route = routeInfo?.routeNumber || record.routeNo || 'Unknown';
        const tripName = route + ' ' + startTime + ' ' + direction;
        tripGroups[record.tripId] = {
          key: record.tripId,
          tripId: record.tripId,
          routeNo: record.routeNo || 'Unknown',
          route,
          direction,
          startTime,
          stopRecords: [],
          stopCount: 0,
          onTimeStops: 0,
          earlyStops: 0,
          lateStops: 0,
          veryLateStops: 0,
          otrPercentage: 0,
          tripDate: record.tripDate || 'Unknown',
          tripName
        };
      }
      
      tripGroups[record.tripId].stopRecords.push(record);
    });
    
    // Process each trip to calculate statistics
    const processedTrips = Object.values(tripGroups).map(trip => {
      // Get unique stops for this specific trip
      const uniqueStops = new Set(trip.stopRecords.map(record => record.stopId));
      trip.stopCount = uniqueStops.size;
      trip.tripName = trip.routeNo + ' ' + trip.direction + ' ' + trip.startTime;
      
      // Count statuses
      trip.stopRecords.forEach(record => {
        if (record.timingStatus === 'on-time') trip.onTimeStops++;
        else if (record.timingStatus === 'early') trip.earlyStops++;
        else if (record.timingStatus === 'late') trip.lateStops++;
        else if (record.timingStatus === 'very-late') trip.veryLateStops++;
      });
      
      // Calculate OTR percentage
      trip.otrPercentage = trip.stopCount > 0 
        ? (trip.onTimeStops / trip.stopCount) * 100 
        : 0;
      
      return trip;
    });
    
    // Sort by route and trip ID
    processedTrips.sort((a, b) => {
      if (a.route !== b.route) return a.route.localeCompare(b.route);
      return a.tripId.localeCompare(b.tripId);
    });
    
    setTripsData(processedTrips);
  }, [reportData, allServices, allRoutes]);
  
  // Memoize the color-related functions to prevent recreations
  const getOtrStatusColor = useCallback((percentage) => {
    if (percentage >= 90) return '#3f8600'; // Success green
    if (percentage >= 80) return '#faad14'; // Warning yellow
    if (percentage >= 70) return '#fa8c16'; // Warning orange
    return '#cf1322'; // Error red
  }, []);
  
  // Process real data when reportData changes
  useEffect(() => {
    if (reportData && reportData.parsedData) {
      console.log('Processing TripStopPerformance data:', {
        dataLength: reportData.parsedData.length,
        sampleRecord: reportData.parsedData[0],
        stopTypeData: reportData.otrAnalysis?.byStopType
      });
      
      aggregateStopData(reportData.parsedData);
      processTripsData();
    } else {
      console.log('No parsed data available in reportData');
    }
  }, [reportData, aggregateStopData, processTripsData]);

  // If no data, show empty state
  if (!reportData || !reportData.summary) {
    return (
      <Card title="Trip & Stop Performance">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size="middle" align="center">
                <Text strong>No performance data available</Text>
                <Text>Select a date range and generate a report to view stop and trip performance metrics</Text>
                <Button type="primary" icon={<CalendarOutlined />} size="large">
                  Generate Report
                </Button>
              </Space>
            }
          />
        </div>
      </Card>
    );
  }

  const { summary, otrAnalysis, routePerformance = { routes: [] } } = reportData;

  // Get stop type data with fallbacks
  const getStopTypeData = (type) => {
    if (!otrAnalysis || !otrAnalysis.byStopType) {
      return { stopType: type, totalStops: 0, onTimeCount: 0, onTimePercentage: 0 };
    }
    
    const stopTypeData = otrAnalysis.byStopType.find(s => s.stopType === type) || 
      { stopType: type, totalStops: 0, onTimeCount: 0, onTimePercentage: 0 };
    
    return stopTypeData;
  };

  // Extract stop position data
  const firstStopData = getStopTypeData('first');
  const middleStopData = getStopTypeData('middle');
  const lastStopData = getStopTypeData('last');

  // Calculate overall OTR
  const overallOTR = otrAnalysis && otrAnalysis.overall ? 
    otrAnalysis.overall.otrPercentage : 
    (summary.onTimePercentage || 0);

  // Stop position OTR data for display
  const stopPositionData = [
    { 
      position: 'First Stops', 
      otrPercentage: firstStopData.onTimePercentage,
      totalStops: firstStopData.totalStops,
      onTimeCount: firstStopData.onTimeCount,
      icon: <ArrowUpOutlined />,
      description: 'First stops are most affected by startup delays and scheduling.',
      color: getOtrStatusColor(firstStopData.onTimePercentage)
    },
    { 
      position: 'Middle Stops', 
      otrPercentage: middleStopData.onTimePercentage,
      totalStops: middleStopData.totalStops,
      onTimeCount: middleStopData.onTimeCount,
      icon: <SwapOutlined />,
      description: 'Middle stops show performance during the trip journey.',
      color: getOtrStatusColor(middleStopData.onTimePercentage)
    },
    { 
      position: 'Last Stops', 
      otrPercentage: lastStopData.onTimePercentage,
      totalStops: lastStopData.totalStops,
      onTimeCount: lastStopData.onTimeCount,
      icon: <ArrowDownOutlined />,
      description: 'Last stops represent arrival performance at destinations.',
      color: getOtrStatusColor(lastStopData.onTimePercentage)
    }
  ];
  
  // Trip completion data
  const tripCompletionData = {
    total: summary.uniqueTrips || 0,
    completeTrips: summary.completeTrips || summary.uniqueTrips || 0,
    incompleteTrips: summary.incompleteTrips || 0,
    averageDuration: summary.averageTripDuration || 0
  };

  // Top routes by number of trips
  const routes = routePerformance.routes || [];
  const topRoutesByTrips = [...routes]
    .sort((a, b) => b.totalTrips - a.totalTrips)
    .slice(0, 5);

  // Create route trip data for table
  const routeTripsTableData = topRoutesByTrips.map((route, index) => ({
    key: `route-trip-${index}`,
    rank: index + 1,
    route: route.route,
    totalTrips: route.totalTrips || 0,
    totalStops: route.totalStops || 0,
    stopsPerTrip: route.totalStops && route.totalTrips ? 
      Math.round(route.totalStops / route.totalTrips * 10) / 10 : 0,
    otrPercentage: route.overallOTR || 0
  }));

  // Create a basic stop sequence visualization
  const stopSequenceData = [
    { 
      sequence: 'Departure',
      otrPercentage: firstStopData.onTimePercentage,
      description: 'Trip departure' 
    },
    { 
      sequence: 'En-Route',
      otrPercentage: middleStopData.onTimePercentage,
      description: 'Mid-journey stops' 
    },
    { 
      sequence: 'Arrival',
      otrPercentage: lastStopData.onTimePercentage,
      description: 'Final destination' 
    }
  ];

  // Get available routes for filtering from real data or report data
  const availableRoutes = allRoutesArray?.length ? 
    allRoutesArray.map(r => ({ value: r.routeId, label: r.routeNumber })) : 
    (routes.length ? 
      routes.map(r => ({ value: r.route, label: `Route ${r.route}` })) : 
      []);

  // Get filtered trips based on selected routes and date range
  const getTripsForDateRange = () => {
    if (!tripsData || !tripsData.length) return [];
    
    // If filters haven't been applied yet, return all data
    if (!applyFilters) return tripsData;
    
    return tripsData.filter(trip => {
      // Filter by date range
      if (tripDateRange && tripDateRange.length === 2) {
        const tripDate = dayjs(trip.tripDate);
        
        // Skip date filtering if the trip date is invalid
        if (tripDate.isValid()) {
          const startDate = tripDateRange[0].startOf('day');
          const endDate = tripDateRange[1].endOf('day');
          
          // Include if date is within range (inclusive)
          const isInDateRange = tripDate.isSame(startDate, 'day') || 
                               tripDate.isSame(endDate, 'day') ||
                               (tripDate.isAfter(startDate) && tripDate.isBefore(endDate));
          
          if (!isInDateRange) return false;
        }
      }
      
      // If no routes selected, include all routes
      if (!selectedRoutes || selectedRoutes.length === 0) return true;
      
      // Filter by route
      if (allServices) {
        const tripService = allServices[trip.tripId];
        const routeId = tripService?.routeId;
        return selectedRoutes.includes(routeId);
      }
      
      // Fallback to route matching from the trip data
      return selectedRoutes.includes(trip.route);
    });
  };

  // Handler for applying filters
  const handleApplyFilters = () => {
    setApplyFilters(true);
  };

  // Handler for resetting filters
  const handleResetFilters = () => {
    // Reset to default date range (last 7 days)
    setTripDateRange(dateRange);
    // Clear selected routes
    setSelectedRoutes([]);
    // Reset the apply filters flag
    setApplyFilters(false);
  };

  // Handle trip selection
  const handleTripSelect = (tripId) => {
    setSelectedTrip(tripId);
    setTripDetailVisible(true);
  };

  // Get stop data for a specific trip
  const getStopData = () => {
    if (!selectedTrip || !reportData?.parsedData?.length) return [];
    
    // Get all stops for this trip
    const tripStops = reportData.parsedData.filter(record => record.tripId === selectedTrip);
    
    // Sort by stop sequence
    tripStops.sort((a, b) => {
      // Try to parse the sequence number
      const seqA = parseInt(a.stopSequence || a.stopSeq || '0', 10);
      const seqB = parseInt(b.stopSequence || b.stopSeq || '0', 10);
      
      if (isNaN(seqA) || isNaN(seqB)) {
        // If we can't parse the sequence, try to use position
        const posA = String(a.stopPosition || '').toLowerCase();
        const posB = String(b.stopPosition || '').toLowerCase();
        
        if (posA === 'first' || posA === 'start') return -1;
        if (posB === 'first' || posB === 'start') return 1;
        if (posA === 'last' || posA === 'end') return 1;
        if (posB === 'last' || posB === 'end') return -1;
        
        // Default to alphabetical by stop name
        return (a.stopName || '').localeCompare(b.stopName || '');
      }
      
      return seqA - seqB;
    });
    
    // Map to the required format
    return tripStops.map(stop => {
      // Try to get status color
      const statusColor = 
        stop.timingStatus === 'on-time' ? '#52c41a' : 
        stop.timingStatus === 'early' ? '#1890ff' : 
        stop.timingStatus === 'late' ? '#faad14' : 
        stop.timingStatus === 'very-late' ? '#cf1322' : 
        '#8c8c8c';
      
      return {
        key: `${stop.stopId}-${stop.stopSequence || stop.stopSeq || '0'}`,
        stopId: stop.stopId,
        stopName: stop.stopName || `Stop ${stop.stopId}`,
        sequence: stop.stopSequence || stop.stopSeq || 'Unknown',
        scheduledTime: stop.scheduledDepartureTime || stop.scheduledArrivalTime || 'Unknown',
        actualTime: stop.actualDepartureTime || stop.actualArrivalTime || 'Unknown',
        variance: stop.varianceInSeconds,
        status: stop.timingStatus,
        otrPercentage: stop.timingStatus === 'on-time' ? 100 : 0
      };
    });
  };

  // Date formatter
  const formatDate = (date) => {
    return dayjs(date).format('YYYY-MM-DD');
  };

  // Get stop level data for selected trip
  const stopData = getStopData();

  // Status tag component
  const StatusTag = ({ status, variance }) => {
    if (status === 'on-time') {
      return <Tag color="green">On Time (+{variance}s)</Tag>;
    } else if (status === 'early') {
      return <Tag color="blue">Early ({variance}s)</Tag>;
    } else if (status === 'late') {
      return <Tag color="orange">Late (+{variance}s)</Tag>;
    } else if (status === 'very-late') {
      return <Tag color="red">Very Late (+{variance}s)</Tag>;
    }
    return <Tag>Unknown</Tag>;
  };

  // Filter stops by name
  const getFilteredStops = () => {
    if (!stopFilterText) return aggregatedStopData;
    
    return aggregatedStopData.filter(stop => 
      stop.stopName.toLowerCase().includes(stopFilterText.toLowerCase()) ||
      stop.stopId.toLowerCase().includes(stopFilterText.toLowerCase())
    );
  };
  
  // Render the stop analysis view
  const renderStopAnalysisView = () => {
    // Filter stops by search text if any
    const filteredStops = stopFilterText
      ? aggregatedStopData.filter(stop => 
          stop.stopName?.toLowerCase().includes(stopFilterText.toLowerCase()) ||
          stop.stopId?.toLowerCase().includes(stopFilterText.toLowerCase())
        )
      : aggregatedStopData;

    const totalStops = filteredStops.length;
    const totalServices = filteredStops.reduce((sum, stop) => sum + stop.totalOccurrences, 0);
    const averageOTR = totalStops > 0 
      ? filteredStops.reduce((sum, stop) => sum + stop.otrPercentage, 0) / totalStops
      : 0;
    
    return (
      <>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Paragraph>
              Analysis of stop performance across all trips. Click on any stop to view detailed performance metrics.
            </Paragraph>
          </Col>
        </Row>
        
        <Divider orientation="left">Stop Summary</Divider>
        
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic 
              title="Total Stops"
              value={totalStops}
              suffix={`/ ${aggregatedStopData.length}`}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic 
              title="Average OTR"
              value={formatPercent(averageOTR)}
              valueStyle={{ color: getOtrStatusColor(averageOTR) }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic 
              title="Total Services"
              value={totalServices}
            />
          </Col>
        </Row>
        
        <Divider orientation="left">Stop Performance Table</Divider>
        
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Input
              placeholder="Search by stop name or ID..."
              prefix={<SearchOutlined />}
              value={stopFilterText}
              onChange={e => setStopFilterText(e.target.value)}
              style={{ marginBottom: 16 }}
              allowClear
            />
            
            <Table
              dataSource={filteredStops}
              rowKey={record => `stop-${record.stopId}`}
              onRow={(record) => ({
                onClick: () => handleStopClick(record),
                className: 'clickable-row'
              })}
              columns={[
                {
                  title: 'Stop',
                  dataIndex: 'stopName',
                  key: 'stopName',
                  render: (text, record) => (
                    <Space direction="vertical" size={1}>
                      <Text strong>{record.stopName}</Text>
                      <Text type="secondary">ID: {record.stopId}</Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.stopName.localeCompare(b.stopName)
                },
                {
                  title: 'Position',
                  dataIndex: 'primaryPosition',
                  key: 'position',
                  render: text => (
                    <Tag color={
                      text === 'First' ? 'blue' : 
                      text === 'Last' ? 'purple' : 'default'
                    }>
                      {text}
                    </Tag>
                  ),
                  filters: [
                    { text: 'First', value: 'First' },
                    { text: 'Middle', value: 'Middle' },
                    { text: 'Last', value: 'Last' }
                  ],
                  onFilter: (value, record) => record.primaryPosition === value
                },
                {
                  title: 'OTR %',
                  dataIndex: 'otrPercentage',
                  key: 'otr',
                  render: value => (
                    <Progress 
                      percent={Math.min(parseFloat((value || 0).toFixed(2))?.toFixed(2), 100)}
                      format={percent => (percent !== undefined && !isNaN(percent)) ? `${percent}%` : '0%'}
                      size="small"
                      status={value >= 80 ? 'success' : (value >= 70 ? 'normal' : 'exception')}
                    />
                  ),
                  sorter: (a, b) => a.otrPercentage - b.otrPercentage,
                  defaultSortOrder: 'descend'
                },
                {
                  title: <>Tracked <ArrowDownOutlined /> %</>,
                  dataIndex: 'trackedArrivalPercentage',
                  key: 'trackedArrivalPercentage',
                  render: value => (
                    <Progress 
                      percent={Math.min(parseFloat((value || 0).toFixed(2)), 100)}
                      format={percent => (percent !== undefined && !isNaN(percent)) ? `${percent}%` : '0%'}
                      size="small"
                      status={value >= 80 ? 'success' : (value >= 70 ? 'normal' : 'exception')}
                    />
                  ),
                  sorter: (a, b) => a.trackedArrivalPercentage - b.trackedArrivalPercentage
                },
                {
                  title: <>Tracked <ArrowUpOutlined /> %</>,
                  dataIndex: 'trackedDeparturePercentage',
                  key: 'trackedDeparturePercentage',
                  render: value => (
                    <Progress 
                      percent={Math.min(parseFloat((value || 0).toFixed(2)), 100)}
                      format={percent => (percent !== undefined && !isNaN(percent)) ? `${percent}%` : '0%'}
                      size="small"
                      status={value >= 80 ? 'success' : (value >= 70 ? 'normal' : 'exception')}
                    />
                  ),
                  sorter: (a, b) => a.trackedDeparturePercentage - b.trackedDeparturePercentage
                },
                {
                  title: 'Services',
                  dataIndex: 'totalOccurrences',
                  key: 'services',
                  sorter: (a, b) => a.totalOccurrences - b.totalOccurrences
                },
                {
                  title: 'On-Time',
                  dataIndex: 'onTimeCount',
                  key: 'onTime',
                  render: (value, record) => `${value} (${formatPercent((value / record.totalOccurrences) * 100)})`
                }
              ]}
              pagination={{ 
                pageSize: tablePageSize,
                defaultPageSize: tablePageSize,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} stops`,
                onShowSizeChange: (current, size) => setTablePageSize(size)
              }}
            />
          </Col>
        </Row>
      </>
    );
  };
  
  // Render daily performance for a stop
  const renderStopDailyPerformance = (stop) => {
    const dateRange = stopDateRange || [];
    const startDate = dateRange[0] ? dateRange[0].startOf('day') : dayjs().subtract(14, 'day');
    const endDate = dateRange[1] ? dateRange[1].endOf('day') : dayjs().endOf('day');
    
    // Filter and sort date performance by the selected date range
    const datePerformance = stop.datePerformanceArray
      .filter(dp => {
        const date = dayjs(dp.date);
        return date.isValid() && date.isBetween(startDate, endDate, null, '[]');
      })
      .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    
    return (
      <div className="stop-daily-performance" style={{ padding: '8px 24px 24px', background: '#fafafa' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={5}>Daily OTR Performance for {stop.stopName}</Title>
          </Col>
        </Row>
        
        <Table
          dataSource={datePerformance}
          rowKey="date"
          size="small"
          columns={[
            {
              title: 'Date',
              dataIndex: 'date',
              key: 'date',
              render: date => dayjs(date).format('DD MMM YYYY (ddd)'),
              sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
              defaultSortOrder: 'descend',
              width: 180
            },
            {
              title: 'OTR %',
              dataIndex: 'otrPercentage',
              key: 'otrPercentage',
              render: value => (
                <Progress 
                  percent={Math.min(parseFloat((value || 0).toFixed(2)), 100)}
                  size="small"
                  strokeColor={getOtrStatusColor(value)}
                />
              ),
              sorter: (a, b) => a.otrPercentage - b.otrPercentage,
              width: 150
            },
            {
              title: 'Services',
              dataIndex: 'totalOccurrences',
              key: 'totalOccurrences',
              sorter: (a, b) => a.totalOccurrences - b.totalOccurrences,
              width: 100,
              align: 'center'
            },
            {
              title: 'On-Time',
              dataIndex: 'onTimeCount',
              key: 'onTimeCount',
              render: (value, record) => `${value} / ${record.totalOccurrences}`,
              width: 100,
              align: 'center'
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => (
                <Button 
                  size="small" 
                  type="primary"
                  onClick={() => handleStopSelect(stop, record.date)}
                  icon={<ClockCircleOutlined />}
                >
                  View Times
                </Button>
              ),
              width: 110,
              align: 'center'
            }
          ]}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </div>
    );
  };
  
  // Function to handle stop click and show modal with detailed stop information
  const handleStopClick = (stop) => {
    setSelectedStop(stop);
    setStopModalVisible(true);
  };

  // Function to get daily breakdown for a specific stop
  const getStopDailyData = (stop) => {
    if (!stop || !reportData?.parsedData?.length) return [];
    
    // Group by date for this specific stop
    const stopData = reportData.parsedData
      .filter(s => s.stopId === stop.stopId)
      .reduce((acc, curr) => {
        const date = curr.tripDate || 'Unknown';
        if (!acc[date]) {
          acc[date] = {
            date,
            totalServices: 0,
            onTime: 0,
            early: 0,
            late: 0,
            veryLate: 0
          };
        }
        
        acc[date].totalServices++;
        
        if (curr.timingStatus === 'on-time') acc[date].onTime++;
        else if (curr.timingStatus === 'early') acc[date].early++;
        else if (curr.timingStatus === 'late') acc[date].late++;
        else if (curr.timingStatus === 'very-late') acc[date].veryLate++;
        
        return acc;
      }, {});
    
    // Convert to array and calculate percentages
    return Object.values(stopData).map(day => ({
      ...day,
      onTimePercentage: (day.onTime / day.totalServices) * 100 || 0,
      earlyPercentage: (day.early / day.totalServices) * 100 || 0,
      latePercentage: (day.late / day.totalServices) * 100 || 0,
      veryLatePercentage: (day.veryLate / day.totalServices) * 100 || 0
    }));
  };

  // Render stop detail modal
  const renderStopDetailModal = () => {
    if (!selectedStop) return null;
    
    const stopDailyData = getStopDailyData(selectedStop);
    
    const positionTypeColor = 
      selectedStop.primaryPosition === 'First' ? 'blue' :
      selectedStop.primaryPosition === 'Last' ? 'purple' : 'default';
    
    return (
      <Modal
        title={
          <Space>
            <EnvironmentOutlined />
            <span>{selectedStop.stopName || `Stop ${selectedStop.stopId}`}</span>
            {selectedStop.isTimingPoint && <Tag color="blue">Timing Point</Tag>}
            {selectedStop.isHighTraffic && <Tag color="orange">High Traffic</Tag>}
          </Space>
        }
        visible={stopModalVisible}
        onCancel={() => setStopModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Summary" key="1">
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic 
                  title="Overall OTR"
                  value={formatPercent(selectedStop.otrPercentage)}
                  valueStyle={{ color: getOtrStatusColor(selectedStop.otrPercentage) }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Total Services"
                  value={selectedStop.totalOccurrences}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Position"
                  value={selectedStop.primaryPosition}
                  valueStyle={{ color: 
                    selectedStop.primaryPosition === 'First' ? '#1890ff' :
                    selectedStop.primaryPosition === 'Last' ? '#722ed1' : '#595959'
                  }}
                />
              </Col>
            </Row>
            
            <Divider>Daily Performance</Divider>
            
            <Table
              dataSource={stopDailyData}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  sorter: (a, b) => new Date(a.date) - new Date(b.date)
                },
                {
                  title: 'Services',
                  dataIndex: 'totalServices',
                  key: 'totalServices',
                  sorter: (a, b) => a.totalServices - b.totalServices
                },
                {
                  title: 'On-Time %',
                  dataIndex: 'onTimePercentage',
                  key: 'onTimePercentage',
                  render: value => (
                    <Progress 
                      percent={Math.min(parseFloat((value || 0).toFixed(2)), 100)}
                      format={percent => (percent !== undefined && !isNaN(percent)) ? `${percent}%` : '0%'}
                      size="small"
                      status={value >= 80 ? 'success' : (value >= 70 ? 'normal' : 'exception')}
                    />
                  ),
                  sorter: (a, b) => a.onTimePercentage - b.onTimePercentage
                }
              ]}
              pagination={{ 
                pageSize: 5,
                defaultPageSize: 5,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} dates`,
                onShowSizeChange: (current, size) => setTablePageSize(size)
              }}
              size="small"
            />
          </TabPane>
          <TabPane tab="Stop Information" key="2">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="Stop Details">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Stop Name">
                      {selectedStop.stopName || `Stop ${selectedStop.stopId}`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Stop ID">
                      {selectedStop.stopId}
                    </Descriptions.Item>
                    <Descriptions.Item label="Position Type">
                      <Tag color={positionTypeColor}>{selectedStop.primaryPosition}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Timing Point">
                      {selectedStop.isTimingPoint ? <Badge status="success" text="Yes" /> : <Badge status="default" text="No" />}
                    </Descriptions.Item>
                    <Descriptions.Item label="High Traffic Area">
                      {selectedStop.isHighTraffic ? <Badge status="warning" text="Yes" /> : <Badge status="default" text="No" />}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Performance Metrics">
                  <div style={{ marginBottom: 16 }}>
                    <Statistic 
                      title="On-Time Running (OTR)" 
                      value={formatPercent(selectedStop.otrPercentage)}
                      valueStyle={{ color: getOtrStatusColor(selectedStop.otrPercentage) }}
                    />
                    <Progress 
                      percent={Math.min(parseFloat((selectedStop.otrPercentage || 0).toFixed(2)), 100)}
                      size="small"
                      status={selectedStop.otrPercentage >= 80 ? 'success' : (selectedStop.otrPercentage >= 70 ? 'normal' : 'exception')}
                    />
                  </div>
                  
                  <Row gutter={[8, 8]}>
                    <Col span={12}>
                      <Card size="small" bordered={false} style={{ background: '#f0f5ff' }}>
                        <Statistic 
                          title="On-Time" 
                          value={selectedStop.onTimeCount || 0}
                          suffix={`/${selectedStop.totalOccurrences}`}
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small" bordered={false} style={{ background: '#e6fffb' }}>
                        <Statistic 
                          title="Early" 
                          value={selectedStop.earlyCount || 0}
                          suffix={`/${selectedStop.totalOccurrences}`}
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small" bordered={false} style={{ background: '#fff7e6' }}>
                        <Statistic 
                          title="Late" 
                          value={selectedStop.lateCount || 0}
                          suffix={`/${selectedStop.totalOccurrences}`}
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small" bordered={false} style={{ background: '#fff1f0' }}>
                        <Statistic 
                          title="Very Late" 
                          value={selectedStop.veryLateCount || 0}
                          suffix={`/${selectedStop.totalOccurrences}`}
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </TabPane>
          <TabPane tab="Routes" key="3">
            <Table
              dataSource={
                (reportData?.parsedData || [])
                  .filter(trip => trip.stops?.some(s => s.stopId === selectedStop.stopId))
                  .map(trip => ({
                    key: `${trip.tripId}-${trip.routeNo}`,
                    tripId: trip.tripId,
                    routeNo: trip.routeNo || 'Unknown',
                    routeName: trip.routeName || 'Unknown',
                    date: trip.tripDate || 'Unknown',
                    direction: trip.direction || 'Unknown'
                  }))
                  .reduce((acc, curr) => {
                    const existingRoute = acc.find(r => r.routeNo === curr.routeNo);
                    if (existingRoute) {
                      existingRoute.tripCount++;
                      if (!existingRoute.dates.includes(curr.date)) {
                        existingRoute.dates.push(curr.date);
                      }
                    } else {
                      acc.push({
                        key: curr.routeNo,
                        routeNo: curr.routeNo,
                        routeName: curr.routeName,
                        tripCount: 1,
                        dates: [curr.date],
                        direction: curr.direction
                      });
                    }
                    return acc;
                  }, [])
              }
              columns={[
                {
                  title: 'Route',
                  dataIndex: 'routeNo',
                  key: 'routeNo',
                  render: (text, record) => (
                    <Space>
                      <Tag color="blue">{text}</Tag>
                      <span>{record.routeName}</span>
                    </Space>
                  )
                },
                {
                  title: 'Direction',
                  dataIndex: 'direction',
                  key: 'direction'
                },
                {
                  title: 'Trips',
                  dataIndex: 'tripCount',
                  key: 'tripCount',
                  sorter: (a, b) => a.tripCount - b.tripCount
                },
                {
                  title: 'Dates',
                  dataIndex: 'dates',
                  key: 'dates',
                  render: dates => `${dates.length} ${dates.length === 1 ? 'day' : 'days'}`
                }
              ]}
              size="small"
              pagination={{ 
                pageSize: tablePageSize,
                defaultPageSize: tablePageSize,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} trips`,
                onShowSizeChange: (current, size) => setTablePageSize(size)
              }}
            />
          </TabPane>
        </Tabs>
      </Modal>
    );
  };

  const handleOpenServiceAnalysis = (routeNo, tripId) => {
    setSelectedInsightRoute(routeNo);
    setSelectedInsightTrip(tripId);
    setShowServiceInsights(true);
  };

  const renderServiceAnalysisModal = () => (
    <Modal
      title={`Service Analysis - Route ${selectedInsightRoute} Trip ${selectedInsightTrip}`}
      open={showServiceInsights}
      onCancel={() => setShowServiceInsights(false)}
      width={1200}
      footer={null}
    >
      <ServiceAnalysis
        dateRange={dateRange}
        reportData={reportData}
        proposedOTRWindowTimetables={processStopData(reportData)}
        selectedRoute={selectedInsightRoute}
        selectedTrip={selectedInsightTrip}
        setSelectedRoute={setSelectedInsightRoute}
        setSelectedTrip={setSelectedInsightTrip}
        initialShowStopTimes={true}
        initialShowScheduledTime={true}
        initialShowProposedTime={false}
        initialShowModelLines={false}
      />
    </Modal>
  );

  return (
    <div className="trip-stop-performance">
      <Card 
        title={<Title level={4}>Trip & Stop OTR Performance</Title>} 
        className="trip-stop-card"
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>View Performance By:</Text>
              <Radio.Group 
                value={displayMode} 
                onChange={e => setDisplayMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="middle"
                style={{ width: '100%' }}
              >
                <Radio.Button value="trips" style={{ width: '33.3%', textAlign: 'center' }}>
                  <Space direction="vertical" size={1}>
                    <CarOutlined />
                    <Text>Trip Analysis</Text>
                  </Space>
                </Radio.Button>
                <Radio.Button value="stopByDay" style={{ width: '33.3%', textAlign: 'center' }}>
                  <Space direction="vertical" size={1}>
                    <CalendarOutlined />
                    <Text>Stops by Day</Text>
                  </Space>
                </Radio.Button>
                <Radio.Button value="stops" style={{ width: '33.3%', textAlign: 'center' }}>
                  <Space direction="vertical" size={1}>
                    <EnvironmentOutlined />
                    <Text>Position Analysis</Text>
                  </Space>
                </Radio.Button>
              </Radio.Group>
              
              <div style={{ marginTop: 30, minHeight: 42, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                {displayMode === 'stops' && (
                  <Text type="secondary">
                    View the OTR performance of stops based on their position in routes (first, middle, last).
                  </Text>
                )}
                {displayMode === 'trips' && (
                  <Text type="secondary">
                    Analyze OTR performance by route and trip with the ability to drill down to individual trips.
                  </Text>
                )}
                {displayMode === 'stopByDay' && (
                  <Text type="secondary">
                    View daily OTR performance for each stop, identifying pattern issues over time.
                  </Text>
                )}
              </div>
            </Space>
          </Col>
        </Row>

        {displayMode === 'stops' ? (
          <>
            {/* Stop Position Analysis */}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Paragraph>
                  Analysis of On-Time Running (OTR) performance based on stop positions within trips. 
                  This shows how performance varies between first, middle, and last stops.
                </Paragraph>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              {stopPositionData.map((data, index) => (
                <Col xs={24} sm={8} key={`stop-pos-${index}`}>
                  <Card 
                    size="small" 
                    title={
                      <Space>
                        {data.icon} 
                        <span>{data.position}</span>
                      </Space>
                    }
                  >
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <Progress
                        type="dashboard"
                        percent={Math.min(parseFloat((data.otrPercentage || 0).toFixed(2)), 100)}
                        format={percent => (percent !== undefined && !isNaN(percent)) ? `${percent}%` : '0%'}
                        width={120}
                        strokeColor={data.color}
                      />
                    </div>
                    <Statistic 
                      title="On-Time Stops"
                      value={`${data.onTimeCount} / ${data.totalStops}`}
                      valueStyle={{ fontSize: 16 }}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {data.description}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
            
            {/* Stop Position Statistics */}
            {/* <Divider orientation="left">Stops by Position</Divider>
            
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small" title="Stops Categorized by Position">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Table 
                        dataSource={[
                          {
                            key: 'first',
                            position: 'First Stops',
                            count: aggregatedStopData.filter(s => s.primaryPosition === 'First').length,
                            totalServices: aggregatedStopData
                              .filter(s => s.primaryPosition === 'First')
                              .reduce((sum, stop) => sum + stop.totalOccurrences, 0),
                            avgOTR: aggregatedStopData.filter(s => s.primaryPosition === 'First').length > 0 
                              ? aggregatedStopData
                                .filter(s => s.primaryPosition === 'First')
                                .reduce((sum, stop) => sum + stop.otrPercentage, 0) / 
                                aggregatedStopData.filter(s => s.primaryPosition === 'First').length
                              : 0,
                            timingPoints: aggregatedStopData
                              .filter(s => s.primaryPosition === 'First' && s.isTimingPoint)
                              .length
                          },
                          {
                            key: 'middle',
                            position: 'Middle Stops',
                            count: aggregatedStopData.filter(s => s.primaryPosition === 'Middle').length,
                            totalServices: aggregatedStopData
                              .filter(s => s.primaryPosition === 'Middle')
                              .reduce((sum, stop) => sum + stop.totalOccurrences, 0),
                            avgOTR: aggregatedStopData.filter(s => s.primaryPosition === 'Middle').length > 0
                              ? aggregatedStopData
                                .filter(s => s.primaryPosition === 'Middle')
                                .reduce((sum, stop) => sum + stop.otrPercentage, 0) / 
                                aggregatedStopData.filter(s => s.primaryPosition === 'Middle').length
                              : 0,
                            timingPoints: aggregatedStopData
                              .filter(s => s.primaryPosition === 'Middle' && s.isTimingPoint)
                              .length
                          },
                          {
                            key: 'last',
                            position: 'Last Stops',
                            count: aggregatedStopData.filter(s => s.primaryPosition === 'Last').length,
                            totalServices: aggregatedStopData
                              .filter(s => s.primaryPosition === 'Last')
                              .reduce((sum, stop) => sum + stop.totalOccurrences, 0),
                            avgOTR: aggregatedStopData.filter(s => s.primaryPosition === 'Last').length > 0
                              ? aggregatedStopData
                                .filter(s => s.primaryPosition === 'Last')
                                .reduce((sum, stop) => sum + stop.otrPercentage, 0) / 
                                aggregatedStopData.filter(s => s.primaryPosition === 'Last').length
                              : 0,
                            timingPoints: aggregatedStopData
                              .filter(s => s.primaryPosition === 'Last' && s.isTimingPoint)
                              .length
                          }
                        ]}
                        columns={[
                          {
                            title: 'Position',
                            dataIndex: 'position',
                            key: 'position',
                            render: (text, record) => (
                              <Tag color={
                                record.key === 'first' ? 'blue' : 
                                record.key === 'last' ? 'purple' : 'default'
                              }>
                                {text}
                              </Tag>
                            )
                          },
                          {
                            title: 'Number of Stops',
                            dataIndex: 'count',
                            key: 'count',
                            sorter: (a, b) => a.count - b.count
                          },
                          {
                            title: 'Total Services',
                            dataIndex: 'totalServices',
                            key: 'totalServices',
                            sorter: (a, b) => a.totalServices - b.totalServices
                          },
                          {
                            title: 'Average OTR',
                            dataIndex: 'avgOTR',
                            key: 'avgOTR',
                            render: value => (
                              <Progress 
                                percent={Math.min(parseFloat((value || 0).toFixed(2)), 100)}
                                format={percent => (percent !== undefined && !isNaN(percent)) ? `${percent}%` : '0%'}
                                size="small"
                                status={value >= 80 ? 'success' : (value >= 70 ? 'normal' : 'exception')}
                              />
                            ),
                            sorter: (a, b) => a.avgOTR - b.avgOTR
                          }
                        ]}
                        pagination={false}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row> */}
          </>
        ) : displayMode === 'trips' ? (
          <>
            {/* Trip Analysis */}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Trip Filters">
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={8}>
                      <Text strong>Date Range:</Text>
                      <RangePicker
                        minDate={dateRange?.[0]}
                        maxDate={dateRange?.[1]}
                        value={tripDateRange}
                        onChange={setTripDateRange}
                        style={{ width: '100%', marginTop: 8 }}
                        allowClear={false}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Text strong>Routes:</Text>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="Select routes"
                        value={selectedRoutes}
                        onChange={setSelectedRoutes}
                        options={availableRoutes}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Space>
                        <Button 
                          type="primary" 
                          icon={<FilterOutlined />}
                          style={{ marginTop: 24 }}
                          onClick={handleApplyFilters}
                        >
                          Apply Filters
                        </Button>
                        <Button
                          style={{ marginTop: 24 }}
                          onClick={handleResetFilters}
                          icon={<CloseCircleOutlined />}
                        >
                          Reset
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Title level={5}>Trip Performance</Title>
                <Space style={{ marginBottom: 8 }}>
                  <Badge status="processing" />
                  <Text>
                    {applyFilters 
                      ? `Showing ${getTripsForDateRange().length} of ${tripsData.length} trips` 
                      : `${tripsData.length} total trips`}
                  </Text>
                  {applyFilters && (
                    <Button type="link" size="small" onClick={handleResetFilters}>
                      View all trips
                    </Button>
                  )}
                </Space>
                <Paragraph>
                  Select a trip to view detailed stop-by-stop OTR performance. 
                  The date range filter allows you to compare performance across different dates.
                </Paragraph>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
              <Col span={24}>
                {applyFilters && (
                  <Alert
                    type="info"
                    message="Filters Applied"
                    description={
                      <Space direction="vertical">
                        <Text>
                          <strong>Date Range:</strong> {formatDate(tripDateRange[0])} to {formatDate(tripDateRange[1])}
                        </Text>
                        {selectedRoutes.length > 0 && (
                          <Text>
                            <strong>Routes:</strong> {selectedRoutes.map(route => availableRoutes.find(r => r.value === route)?.label || route).join(', ')}
                          </Text>
                        )}
                        <Text>
                          <strong>Results:</strong> {getTripsForDateRange().length} trips found
                        </Text>
                      </Space>
                    }
                    style={{ marginBottom: 16 }}
                    showIcon
                    closable
                    onClose={() => setApplyFilters(false)}
                  />
                )}
                <Table 
                  dataSource={getTripsForDateRange()}
                  columns={[
                    {
                      title: 'Trip',
                      dataIndex: 'tripName',
                      key: 'tripName',
                      render: tripName => <strong>{tripName}</strong>
                    },
                    {
                      title: 'Route',
                      dataIndex: 'route',
                      key: 'route',
                      sorter: (a, b) => a.route.localeCompare(b.route)
                    },
                    {
                      title: 'Start Time',
                      dataIndex: 'startTime',
                      key: 'startTime',
                      sorter: (a, b) => a.startTime.localeCompare(b.startTime)
                    },
                    {
                      title: 'Stops',
                      dataIndex: 'stopCount',
                      key: 'stopCount',
                      sorter: (a, b) => a.stopCount - b.stopCount
                    },
                    {
                      title: 'OTR',
                      dataIndex: 'otrPercentage',
                      key: 'otrPercentage',
                      render: value => (
                        <Progress 
                          percent={value?.toFixed(2)} 
                          size="small" 
                          status={value > 80 ? 'success' : (value > 70 ? 'normal' : 'exception')}
                        />
                      ),
                      sorter: (a, b) => a.otrPercentage - b.otrPercentage
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_, record) => (
                        <Space>
                          {/* <Button 
                            type="primary" 
                            size="small" 
                            icon={<SearchOutlined />} 
                            onClick={() => handleTripSelect(record.tripId)}
                          >
                            View Stops
                          </Button> */}
                          <Button
                            type="default"
                            size="small"
                            icon={<LineChartOutlined />}
                            onClick={() => handleOpenServiceAnalysis(record.routeNo, record.tripId)}
                          >
                            Service Analysis
                          </Button>
                        </Space>
                      )
                    }
                  ]}
                  pagination={{ 
                    pageSize: tablePageSize,
                    defaultPageSize: tablePageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ['5', '10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} trips`,
                    onShowSizeChange: (current, size) => setTablePageSize(size)
                  }}
                />
              </Col>
            </Row>
          </>
        ) : (
          <>{renderStopAnalysisView()}</>
        )}
      </Card>

      {/* Trip Detail Modal */}
      <Modal
        title={
          <Space>
            <CarOutlined />
            <span>Trip Detail: {selectedTrip}</span>
          </Space>
        }
        open={tripDetailVisible}
        onCancel={() => setTripDetailVisible(false)}
        footer={[
          <Button key="back" onClick={() => setTripDetailVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedTrip && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small">
                  <Space size="large">
                    <Statistic 
                      title="Date Range" 
                      value={`${formatDate(tripDateRange[0])} to ${formatDate(tripDateRange[1])}`}
                      style={{ fontSize: 14 }}
                    />
                    <Statistic 
                      title="OTR Performance" 
                      value={formatPercent(tripsData.find(t => t.tripId === selectedTrip)?.otrPercentage || 0)}
                      valueStyle={{ color: getOtrStatusColor(tripsData.find(t => t.tripId === selectedTrip)?.otrPercentage || 0) }}
                    />
                    <Statistic 
                      title="Total Stops" 
                      value={tripsData.find(t => t.tripId === selectedTrip)?.stopCount || 0}
                    />
                    <Statistic 
                      title="Route" 
                      value={tripsData.find(t => t.tripId === selectedTrip)?.route || ''}
                    />
                  </Space>
                </Card>
              </Col>
            </Row>

            <Divider>Stop Level Performance</Divider>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Table
                  dataSource={stopData}
                  columns={[
                    {
                      title: 'Stop',
                      dataIndex: 'stopName',
                      key: 'stopName',
                      width: '30%',
                      render: (_, record) => (
                        <Space>
                          <strong>{record.stopName}</strong>
                          <Tag color={
                            record.primaryPosition === 'First' ? 'blue' : 
                            record.primaryPosition === 'Last' ? 'purple' : 'default'
                          }>{record.primaryPosition}</Tag>
                        </Space>
                      )
                    },
                    {
                      title: 'Scheduled',
                      dataIndex: 'scheduledTime',
                      key: 'scheduledTime'
                    },
                    {
                      title: 'Actual',
                      dataIndex: 'actualTime',
                      key: 'actualTime'
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      render: (status, record) => (
                        <StatusTag status={status} variance={record.variance} />
                      ),
                      filters: [
                        { text: 'On Time', value: 'on-time' },
                        { text: 'Early', value: 'early' },
                        { text: 'Late', value: 'late' },
                        { text: 'Very Late', value: 'very-late' }
                      ],
                      onFilter: (value, record) => record.status === value
                    }
                  ]}
                  pagination={false}
                  size="small"
                  rowClassName={(record) => {
                    if (record.status === 'on-time') return 'ontime-row';
                    if (record.status === 'early') return 'early-row';
                    if (record.status === 'late') return 'late-row';
                    if (record.status === 'very-late') return 'verylate-row';
                    return '';
                  }}
                />
              </Col>
            </Row>

            <Divider>Stop Timeline</Divider>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Timeline mode="left">
                  {stopData.map((stop) => (
                    <Timeline.Item 
                      key={stop.key}
                      color={
                        stop.status === 'on-time' ? 'green' :
                        stop.status === 'early' ? 'blue' :
                        stop.status === 'late' ? 'orange' : 'red'
                      }
                      label={stop.scheduledTime}
                    >
                      <Space direction="vertical">
                        <Text strong>{stop.stopName}</Text>
                        <Space>
                          <Text type="secondary">Actual: {stop.actualTime}</Text>
                          <StatusTag status={stop.status} variance={stop.variance} />
                        </Space>
                      </Space>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Col>
            </Row>

            <Divider>Stop Time Analysis</Divider>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <StopTimeAnalysis
                  reportData={reportData}
                  selectedRoute={tripsData.find(t => t.tripId === selectedTrip)?.routeNo}
                  selectedTrip={selectedTrip}
                  setSelectedRoute={() => {}}
                  setSelectedTrip={() => {}}
                  dateRange={tripDateRange}
                />
              </Col>
            </Row>
          </>
        )}
      </Modal>
      
      {/* Stop Detail Modal */}
      {renderStopDetailModal()}
      {renderServiceAnalysisModal()}
    </div>
  );
};

export default TripStopPerformance; 